import { size, sumBy } from "lodash";
import {
  type AddressReadDto,
  type CatalogMediumGetCatalogMediumConfiguratorReadDto,
  type OrderGetPaginatedOrdersOrderReadDto,
  type OrderItemDetailsReadDto,
  type OrderItemDetailsSandboxItemReadDto,
  type OrderItemVersionCreateCommand,
} from "@/config/apiClient";
import { z } from "@/helpers/forms/zod";
import { tolgee } from "@/providers/TranslationProvider";

// Define new order key
export const newOrderKey = "newOrder";

export const catalogMediumFormSchema = (
  configurators: Array<CatalogMediumGetCatalogMediumConfiguratorReadDto>,
  orders?: Array<OrderGetPaginatedOrdersOrderReadDto>,
  orderItemId?: string
) =>
  z
    .object({
      orderId: z.string().optional(),
      orderName: z.string().optional(),
      name: z.string().trim().optional(),
      configuration: z
        .array(z.string().uuid().optional())
        .length(size(configurators))
        .optional(),
      quantity: z.number().int().gte(0),
      version: z.number().int().gt(0).lt(100),
      specificRequest: z.string().optional(),
      deliveryDate: z.date().nullable().optional(),
      withPreMedia: z.boolean().optional(),
    })
    .superRefine((value, ctx) => {
      // Select Existing or new order required
      if (!value.orderId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["orderId"],
        });
      }

      if (value.name) {
        // Product Name do not respect length condition
        if (value.name.length < 3 || value.name.length > 40) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["name"],
            message: tolgee.t(
              "order.mediumDetails.stepConfiguration.productNameError"
            ),
          });
        }

        const selectedOrder = orders?.find(
          (order) => order.id === value.orderId
        );
        const isDuplicateName = selectedOrder?.OrderItem.some(
          (item) =>
            item.sandboxItem?.name?.toLowerCase() ===
              value.name?.toLowerCase() && item.id !== orderItemId
        );
        // Product name already exist
        if (isDuplicateName) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["name"],
            message: tolgee.t(
              "order.mediumDetails.stepConfiguration.duplicateNameError"
            ),
          });
        }
      }

      if (value.orderId === newOrderKey) {
        // OrderName required for new order
        if (!value.orderName) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["orderName"],
            message: tolgee.t(
              "order.mediumDetails.stepConfiguration.orderNameMissing"
            ),
          });
        } else {
          // OrderName do not respect length condition
          if (value.orderName.length < 3 || value.orderName.length > 40) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["orderName"],
              message: tolgee.t(
                "order.mediumDetails.stepConfiguration.orderNameError"
              ),
            });
          }
        }
      }
    });

export type CatalogMediumFormSchema = z.infer<
  ReturnType<typeof catalogMediumFormSchema>
>;

function getMatchingOptionIds(
  sandboxItem: OrderItemDetailsSandboxItemReadDto,
  configurators: Array<CatalogMediumGetCatalogMediumConfiguratorReadDto>
) {
  const configuratorOptionIds = new Set(
    sandboxItem.SandboxItemToConfiguratorOptions.map(
      (item) => item.configuratorOptionId
    )
  );
  const result = configurators.flatMap((item) => {
    return item.options
      .filter((option) => configuratorOptionIds.has(option.id))
      .map((option) => option.id);
  });
  return result;
}

export const getDefaultValuesForCatalogMediumForm = ({
  configurators,
  orderId,
}: {
  configurators:
    | Array<CatalogMediumGetCatalogMediumConfiguratorReadDto>
    | undefined;
  orderId?: string;
}): CatalogMediumFormSchema => ({
  orderId: orderId,
  orderName: undefined,
  name: "",
  configuration: configurators
    ?.filter((c) => c.options.length > 0)
    .map((configurator) => {
      const defaultOption = configurator?.options?.find(
        (elem) => elem.isDefault
      );

      return (
        defaultOption?.id ??
        (configurator.options.length > 0
          ? configurator.options[0].id
          : undefined)
      );
    }),
  quantity: 1,
  version: 1,
  specificRequest: "",
  withPreMedia: undefined,
});

export const buildSandboxItemVersionsWithAddress = ({
  versionCount,
  quantity,
  address,
}: {
  versionCount: number;
  quantity: number;
  address: AddressReadDto;
}): OrderItemVersionCreateCommand[] =>
  Array.from({ length: versionCount }, (_, index) => ({
    name: `V${index + 1}`,
    sandboxItemVersionDispatch: [
      {
        quantity: index === 0 ? quantity : 0,
        address: {
          name: address.name,
          address: address.address,
          city: address.city,
          postalCode: address.postalCode,
          country: address.country,
          recipientName: address.recipientName ?? undefined,
          complementaryInfo: address.complementaryInfo ?? undefined,
          phoneNumber: address.phoneNumber ?? undefined,
        },
      },
    ],
  }));

export const getEditionDefaultValuesForCatalogMediumForm = ({
  orderItem,
  configurators,
}: {
  orderItem: OrderItemDetailsReadDto;
  configurators: Array<CatalogMediumGetCatalogMediumConfiguratorReadDto>;
}): CatalogMediumFormSchema => ({
  orderId: orderItem.orderId,
  orderName: orderItem.order.name,
  deliveryDate: orderItem.deliveryDate
    ? new Date(orderItem.deliveryDate)
    : undefined,
  name:
    orderItem.sandboxItem.catalogMedium?.name !== orderItem.sandboxItem.name
      ? orderItem.sandboxItem.name
      : "",
  configuration: getMatchingOptionIds(orderItem.sandboxItem, configurators),
  quantity: sumBy(orderItem.sandboxItem.SandboxItemVersion, (version) =>
    sumBy(version.SandboxItemVersionDispatch, "quantity")
  ),
  version: orderItem.sandboxItem.SandboxItemVersion?.length || 1,
  specificRequest: orderItem.sandboxItem.specificRequest ?? "",
  withPreMedia: Boolean(orderItem.sandboxItem.withPreMedia),
});
